import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';

type Props = {
  hasEndContent?: boolean;
  hasInsideLabel?: boolean;
  hasStartContent?: boolean;
  id: string;
  isDisabled?: boolean;
  isFocused?: boolean;
  text: string;
  variant: 'prefix' | 'suffix';
};

export function PrefixSuffix({
  hasEndContent,
  hasInsideLabel,
  hasStartContent,
  id,
  isDisabled,
  isFocused,
  text,
  variant
}: Props) {
  const prefixSuffixStyles = {
    base: cssProps`${{
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      lineHeight: 'inherit',
      fontWeight: 'inherit',
      fontSize: '100%',
      border: '0',
      backgroundColor: 'transparent',
      color: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-color)'
    }}`,
    isFocused: cssProps`${{
      opacity: '.45'
    }}`,
    isDisabled: cssProps`${{
      color: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-color-disabled)'
    }}`,
    insideLabel: cssProps`${{
      minHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-inside-edge-height)',
      paddingTop: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-inside-edge-padding-top)'
    }}`,
    prefixHasStartContent: cssProps`${{
      paddingInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-icon-to-text-padding)'
    }}`,

    suffixHasEndContent: cssProps`${{
      paddingInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-icon-to-text-padding)'
    }}`,
    prefixInlineStart: cssProps`${{
      paddingInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding)'
    }}`,
    suffixInlineStart: cssProps`${{
      paddingInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding)'
    }}`
  };

  // We want the input to have padding on itself. This gives the user more space to click on to set focus to the input.
  // The prefix/suffix need padding between itself and either the 'edge' or the 'startContent'/'endContent'.
  // So the logic is:
  // if no startContent and we have a prefix, add --text-to-edge-padding to the prefix
  // if no endContent and we have a suffix, add --text-to-edge-padding to the suffix
  // if startContent and we have a prefix, add text-field-icon-to-text-padding
  // if endContent and we have a suffix, add text-field-icon-to-text-padding
  // **-- text-to-edge-padding**prefix (input has padding for prefix/input)input(input has padding for suffix/input)suffix**--text-to-edge-padding**
  // -- (this should be in startContent)startContent **icon-to-text-padding**prefix (input has padding for prefix/input)input(input has padding for suffix/input) suffix **--text-to-edge-padding**
  // -- **icon-to-text-padding**prefix (input has padding for prefix/input)input(input has padding for suffix/input) suffix**--icon-to-text-padding**(this should be in endContent)endContent
  const prefixSuffixClasses = classNames([
    prefixSuffixStyles.base,
    variant === 'suffix' && hasEndContent && prefixSuffixStyles.suffixHasEndContent,
    hasInsideLabel && prefixSuffixStyles.insideLabel,
    variant === 'prefix' && hasStartContent && prefixSuffixStyles.prefixHasStartContent,
    isFocused && prefixSuffixStyles.isFocused,
    isDisabled && prefixSuffixStyles.isDisabled,
    variant === 'prefix' && !hasStartContent && prefixSuffixStyles.prefixInlineStart,
    variant === 'suffix' && !hasEndContent && prefixSuffixStyles.suffixInlineStart
  ]);

  return (
    <div class={prefixSuffixClasses} id={id}>
      <span>{text}</span>
    </div>
  );
}
